import type { ReactNode } from 'react'
import { AlertCircle, RefreshCw, Unplug } from 'lucide-react'
import type { GiftSummaryDataStatus } from '@/lib/api'
import { cn } from '@/lib/utils'

/** 件数をカンマ区切りで返す（null は '—'） */
export function fmtInt(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return value.toLocaleString('ja-JP')
}

/** 率（0..1）を % 表記で返す。null は '—' */
export function fmtPct(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return `${(value * 100).toFixed(1)}%`
}

/** 金額（円）をカンマ区切りで返す。null は '—' */
export function fmtYen(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return `${value.toLocaleString('ja-JP')}円`
}

export function PageHeader({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
      {sub && <p className="text-sm text-muted-foreground">{sub}</p>}
    </div>
  )
}

export function Section({
  title,
  aside,
  note,
  children,
}: {
  title: string
  aside?: ReactNode
  note?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {aside}
      </div>
      {children}
      {note && <p className="mt-2 text-xs text-muted-foreground">{note}</p>}
    </section>
  )
}

export function KpiCard({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub?: ReactNode
}) {
  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums tracking-tight">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  )
}

export function LoadingRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-muted-foreground" role="status">
      <RefreshCw className="h-4 w-4 animate-spin" aria-hidden />
      {label}を読み込み中…
    </div>
  )
}

export function ErrorRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-red-600" role="status">
      <AlertCircle className="h-4 w-4" aria-hidden />
      {label}の取得に失敗しました
    </div>
  )
}

/**
 * dataStatus に応じた共通の空 / 未接続表示。
 * - not_connected: 未接続（何が接続されれば何が出るかを note で示す）
 * - empty: データなし（期間に件数が無い）
 */
export function StatusNotice({
  dataStatus,
  note,
}: {
  dataStatus: GiftSummaryDataStatus
  note?: ReactNode
}) {
  if (dataStatus === 'not_connected') {
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        <Unplug className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div>
          <p className="font-medium">未接続</p>
          {note && <p className="text-amber-700">{note}</p>}
        </div>
      </div>
    )
  }
  if (dataStatus === 'empty') {
    return (
      <p className="py-4 text-sm text-muted-foreground" role="status">
        この期間の計測データはありません
      </p>
    )
  }
  return null
}

/** 更新時刻の表示 */
export function AsOfLabel({ computedAt }: { computedAt: string | null }) {
  if (!computedAt) return <span className="text-xs text-muted-foreground">更新時刻 未計測</span>
  const label = new Date(computedAt).toLocaleString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
  return <span className="text-xs text-muted-foreground">更新 {label}</span>
}

export function TogglePill({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md border px-3 py-1 text-sm',
        active ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground',
      )}
    >
      {children}
    </button>
  )
}
